import { env } from '../config/env';

export interface MenuItem {
  id: number;
  tenantId?: number;
  label: string;
  description?: string;
  icon?: string;
  route: string;
  parentId?: number;
  sortOrder: number;
  isActive: boolean;
  children: MenuItem[];
}

interface MenuResponse {
  success?: boolean;
  data?: {
    items: MenuItem[];
    total: number;
  };
  items?: MenuItem[];
  total?: number;
  totalCount?: number;
  page?: number;
  pageSize?: number;
}

const API_BASE_URL = env.apiBaseUrl || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

type ApiMenuItem = {
  id?: number;
  menuId?: number;
  tenantId?: number;
  label?: string;
  description?: string | null;
  icon?: string | null;
  route?: string | null;
  parentId?: number | null;
  sortOrder?: number;
  isActive?: boolean;
  children?: ApiMenuItem[] | null;
  // PascalCase (ASP.NET default) fallback
  Children?: ApiMenuItem[] | null;
};

const normalizeMenuItem = (item: ApiMenuItem & Record<string, unknown>): MenuItem => {
  const rawChildren = item.children ?? item.Children;
  const children = Array.isArray(rawChildren) ? rawChildren : [];

  const raw = item as ApiMenuItem & Record<string, unknown>;
  return {
    id: (raw.id ?? raw.menuId ?? raw.MenuId ?? 0) as number,
    tenantId: (raw.tenantId ?? raw.TenantId) as number | undefined,
    label: (raw.label ?? raw.Label ?? '') as string,
    description: (raw.description ?? raw.Description) as string | undefined,
    icon: (raw.icon ?? raw.Icon) as string | undefined,
    route: (raw.route ?? raw.Route ?? '') as string,
    parentId: (raw.parentId ?? raw.ParentId) as number | undefined,
    sortOrder: (raw.sortOrder ?? raw.SortOrder ?? 0) as number,
    isActive: typeof raw.isActive === 'boolean' ? raw.isActive : (typeof raw.IsActive === 'boolean' ? raw.IsActive : true),
    children: children.map(normalizeMenuItem),
  };
};

const extractItems = (data: any): ApiMenuItem[] => {
  // Supports camelCase and PascalCase (ASP.NET may return Items/Children)
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.Items)) return data.data.Items;
  return [];
};

export const menuAPI = {
  /**
   * Get all menus (optionally filtered by tenant and active status)
   * @param tenantId - Optional tenant ID filter
   * @param isActive - Optional active status filter (true = active only)
   * @returns Promise<MenuItem[]> - Array of menu items (flat list)
   */
  getAll: async (tenantId?: number, isActive?: boolean): Promise<MenuItem[]> => {
    try {
      const params = new URLSearchParams();
      if (tenantId !== undefined) {
        params.append('tenantId', tenantId.toString());
      }
      if (isActive !== undefined) {
        params.append('isActive', isActive.toString());
      }
      
      const url = `${API_BASE_URL}/v1/menus${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch menus: ${response.statusText}`);
      }

      const data: MenuResponse = await response.json();
      return extractItems(data).map(normalizeMenuItem);
    } catch (error) {
      console.error('Error fetching menus:', error);
      throw error;
    }
  },

  /**
   * Get menu tree (hierarchical structure for sidebar/navigation)
   * @param tenantId - Optional tenant ID filter
   * @param isActive - Optional active status filter (true = active only)
   * @returns Promise<MenuItem[]> - Root menu items with children populated
   */
  getTree: async (tenantId?: number, isActive?: boolean): Promise<MenuItem[]> => {
    try {
      const params = new URLSearchParams();
      if (tenantId !== undefined) {
        params.append('tenantId', tenantId.toString());
      }
      if (isActive !== undefined) {
        params.append('isActive', isActive.toString());
      }

      const url = `${API_BASE_URL}/v1/menus/tree${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch menu tree: ${response.statusText}`);
      }

      const data = await response.json();
      const roots = extractItems(data).map(normalizeMenuItem);
      return roots
    } catch (error) {
      console.error('Error fetching menu tree:', error);
      throw error;
    }
  },

  /**
   * Get Identity (core) menu snapshot for a tenant – source of truth for "which routes exist".
   * Used to compute truly missing menus before showing "X menüyü oluştur" (avoids Reporting sync lag).
   */
  getCoreSnapshot: async (tenantId: number): Promise<{ route: string }[]> => {
    try {
      const url = `${API_BASE_URL}/v1/menus/existing-routes?tenantId=${tenantId}`;
      const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error(`Failed to fetch core snapshot: ${response.statusText}`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);
      const withRoute = (list as { route?: string }[]).filter((m) => m?.route != null).map((m) => ({ route: m.route! }));
      // Deduplicate by route (backend may return same route more than once)
      const seen = new Set<string>();
      return withRoute.filter((m) => {
        if (seen.has(m.route)) return false;
        seen.add(m.route);
        return true;
      });
    } catch (error) {
      console.error('Error fetching menu core snapshot:', error);
      throw error;
    }
  },

  /**
   * Get menu by ID
   * @param id - Menu ID
   * @returns Promise<MenuItem>
   */
  getById: async (id: number): Promise<MenuItem> => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/menus/${id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch menu: ${response.statusText}`);
      }

      const data = await response.json();
      // This endpoint returns a single item (not list). Normalize in case backend uses menuId.
      const item = (data?.data ?? data) as ApiMenuItem;
      return normalizeMenuItem(item);
    } catch (error) {
      console.error('Error fetching menu:', error);
      throw error;
    }
  },

  /**
   * Create a new menu
   * Backend expects CreateMenuCommand: tenantId, label, description, icon, route, parentId, sortOrder, isActive (camelCase)
   * @param menu - Menu data
   * @returns Promise<MenuItem>
   */
  create: async (menu: Partial<MenuItem>): Promise<MenuItem> => {
    try {
      const body = {
        tenantId: menu.tenantId ?? null,
        label: menu.label ?? '',
        description: menu.description ?? null,
        icon: menu.icon ?? null,
        route: menu.route ?? '',
        parentId: menu.parentId ?? null,
        sortOrder: menu.sortOrder ?? 0,
        isActive: menu.isActive ?? true,
      };
      const response = await fetch(`${API_BASE_URL}/v1/menus`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let message = response.statusText;
        try {
          const errBody = await response.json();
          if (errBody?.message) message = errBody.message;
        } catch {
          // ignore parse error
        }
        throw new Error(`Failed to create menu: ${message}`);
      }

      const data = await response.json();
      const item = (data?.data ?? data) as ApiMenuItem;
      return normalizeMenuItem(item);
    } catch (error) {
      console.error('Error creating menu:', error);
      throw error;
    }
  },

  /**
   * Update an existing menu
   * Backend expects UpdateMenuCommand: Id, Label, Description, Icon, Route, ParentId, SortOrder, IsActive
   * @param id - Menu ID (must match URL and body)
   * @param menu - Menu data
   * @returns Promise<MenuItem>
   */
  update: async (id: number, menu: Partial<MenuItem>): Promise<MenuItem> => {
    try {
      const body = {
        Id: id,
        Label: menu.label ?? '',
        Description: menu.description ?? null,
        Icon: menu.icon ?? null,
        Route: menu.route ?? '',
        ParentId: menu.parentId ?? null,
        SortOrder: menu.sortOrder ?? 0,
        IsActive: menu.isActive ?? true,
      };
      const response = await fetch(`${API_BASE_URL}/v1/menus/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let message = response.statusText;
        try {
          const err = await response.json() as { message?: string };
          if (err?.message) message = err.message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      const data = await response.json();
      const item = (data?.data ?? data) as ApiMenuItem;
      return normalizeMenuItem(item);
    } catch (error) {
      console.error('Error updating menu:', error);
      throw error;
    }
  },

  /**
   * Sync the canonical BIArtHR sidebar menu catalog for a tenant (roots + sub-menus).
   * Creates missing menus and updates existing ones with correct parent-child relationships.
   */
  syncCatalog: async (params: { tenantId: number; dryRun?: boolean; disableNotInCatalog?: boolean }) => {
    const body = {
      tenantId: params.tenantId,
      dryRun: params.dryRun ?? false,
      disableNotInCatalog: params.disableNotInCatalog ?? true,
    };
    const response = await fetch(`${API_BASE_URL}/v1/menus/sync-catalog`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { message?: string };
      throw new Error(err?.message ?? `Sync catalog failed: ${response.statusText}`);
    }
    return response.json() as Promise<{ success: boolean; data: { totalDefinitions: number; inserted: number; updated: number; changes: unknown[] } }>;
  },

  /**
   * Delete a menu
   * @param id - Menu ID
   * @returns Promise<void>
   */
  delete: async (id: number): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/menus/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete menu: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      throw error;
    }
  },
};
